import { ChangeEvent, FC, Fragment, useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { debounce } from 'throttle-debounce';
// Models
import IContact from 'app/models/Contact';
// Types
import ContactTypes from 'app/types/ContactTypes';
import PhoneTypes from 'app/types/PhoneTypes';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { createContact, getContactsAutocomplete, updateContact } from 'app/store/Contacts/Contacts.async';
// Selectors
import { selectContactsAsOptions, selectLoading, selectAutocompleteLoading } from 'app/store/Contacts/Contacts.selectors';
// MUI
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
// Icons
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
// Components
import Dialog from 'app/components/Dialog';
import { Button, IconButton, Input, Select } from 'app/components/Mui';
import Phone from 'app/components/Phone';
// Utilities
import { isEmailValid, isRequired } from 'app/utilities/Validations';
// i18next
import { useTranslation } from 'react-i18next';

interface IFormData extends Omit<IContact, 'id'>{};

type Props = {
  open: boolean;
  onClose: () => void;
  contact?: IContact;
  onCreated?: (contact?:IContact) => void;
}

const ContactsFormDialog:FC<Props> = ({
  // Props
  contact, open, onClose, onCreated
}) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const loading = useAppSelector(selectLoading);
  const contactsOptions = useAppSelector(selectContactsAsOptions);
  const autocompleteLoading = useAppSelector(selectAutocompleteLoading);

  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<IFormData>({
    defaultValues: {
      name: contact?.name || '',
      type: contact?.type || ContactTypes.Person,
      firstName: contact?.firstName || '',
      middleName: contact?.middleName || '',
      lastName: contact?.lastName || '',
      email: contact?.email || '',
      website: contact?.website || '',
      phones: contact?.phones || [],
      address: contact?.address,
      notes: contact?.notes || '',
      referenceContact: contact?.referenceContact
    }
  });

  const { fields, append, remove:onRemovePhone } = useFieldArray({
    control,
    name: 'phones'
  });

  const onAddPhone = () => {
    append({ type: PhoneTypes.Main, phoneNumber: '', extension: '' });
  }

  const onAddAddress = () => {
    setValue('address', { address: '', city: '', state: '', zipCode: '', country: 'United States' });
  }

  const onRemoveAddress = () => {
    setValue('address', undefined);
  }

  const typeWatcher = watch('type');
  const addressWatcher = watch('address');

  const onSubmit = handleSubmit((data:IFormData) => {
    const {
      name, firstName, middleName, lastName, type, email,
      website, notes, phones, address, referenceContact
    } = data;
    const nextData:any = {
      type
    };
    if ( type === ContactTypes.Company ) nextData['name'] = name;
    if ( type === ContactTypes.Person ) {
      nextData['firstName'] = firstName;
      nextData['lastName'] = lastName;
      if ( middleName ) nextData['middleName'] = middleName;
    }
    if ( email ) nextData['email'] = email;
    if ( website ) nextData['website'] = website;
    if ( notes ) nextData['notes'] = notes;
    if ( phones?.length ) nextData['phones'] = phones;
    if ( address && address.address ) nextData['address'] = address;
    if ( referenceContact ) nextData['referenceContactId'] = referenceContact.id;

    if ( contact?.id ){
      asyncUpdateContact({ contactId: contact.id, data: nextData });
    } else {
      asyncCreateContact(nextData);
    }
  });

  const asyncCreateContact = async (data:IFormData) => {
    try {
      const contact = await dispatch(createContact(data)).unwrap();
      if ( onCreated ) onCreated(contact);
      onClose();
    } catch(err){}
  }

  const asyncUpdateContact = async (data:{ contactId:number; data:IFormData }) => {
    try {
      await dispatch(updateContact(data)).unwrap();
      onClose();
    } catch(err){}
  }

  // eslint-disable-next-line
  const debounceContacts = useCallback(debounce(500, (search:string) => {
    dispatch(getContactsAutocomplete({ search, limit: 50, type: ContactTypes.Company }));
  }), []);

  const handleChangeContactInput = (event:ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    debounceContacts(value);
  }

  const typeOptions = useMemo(() => [
    { id: ContactTypes.Person, name: t('labels.person') },
    { id: ContactTypes.Company, name: t('labels.company') }
  ], [t]);

  const phoneTypeOptions = useMemo(() => [
    { id: PhoneTypes.Main, name: t('labels.main') },
    { id: PhoneTypes.Mobile, name: t('labels.mobile') },
    { id: PhoneTypes.Work, name: t('labels.work') },
    { id: PhoneTypes.Home, name: t('labels.home') },
    { id: PhoneTypes.Fax, name: t('labels.fax') }
  ], [t]);

  useEffect(() => {
    dispatch(getContactsAutocomplete({ limit: 50, type: ContactTypes.Company }));
    // eslint-disable-next-line
  }, []);

  const actions = (
    <Fragment>
      <Button
        name="Cancel contact dialog"
        onClick={onClose}
      >{t('buttons.close')}</Button>
      <Button
        name={`${contact ? 'update' : 'create'} contact dialog`}
        loading={loading}
        onClick={onSubmit}
        variant="contained"
        color="primary"
      >{contact ? t('buttons.update') : t('buttons.create')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={contact ? t('pages.contacts.updateFormTitle') : t('pages.contacts.createFormTitle')}
      actions={actions}
    >
      <form noValidate>
        {!contact ? (
          <Controller
            control={control} name="type"
            rules={{ required: isRequired }}
            render={({ field }) => (
              <Select
                {...field}
                options={typeOptions}
                label={t('labels.type')}
                error={Boolean(errors.type)}
                helperText={errors.type?.message || ''}
                required
              />
            )}
          />
        ) : null}
        {typeWatcher === ContactTypes.Company ? (
          <Controller
            control={control} name="name"
            rules={{ required: isRequired }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('labels.name')}
                error={Boolean(errors.name)}
                helperText={errors.name?.message || ''}
                required
              />
            )}
          />
        ) : (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              control={control} name="firstName"
              rules={{ required: isRequired }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('labels.firstName')}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName?.message || ''}
                  required
                />
              )}
            />
            <Controller
              control={control} name="middleName"
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('labels.middleName')}
                />
              )}
            />
            <Controller
              control={control} name="lastName"
              rules={{ required: isRequired }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('labels.lastName')}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName?.message || ''}
                  required
                />
              )}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            control={control} name="email"
            rules={{ pattern: isEmailValid }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('labels.email')}
                error={Boolean(errors.email)}
                helperText={errors.email?.message || ''}
              />
            )}
          />
          <Controller
            control={control} name="website"
            render={({ field }) => (
              <Input
                {...field}
                label={t('labels.website')}
              />
            )}
          />
        </Box>
        {typeWatcher === ContactTypes.Person ? (
          <Controller
            control={control} name="referenceContact"
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                value={value}
                onChange={(_:any, nextValue:any) => onChange(nextValue)}
                options={contactsOptions || []}
                onClose={() => debounceContacts('')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('labels.company')}
                    fullWidth
                    margin="normal"
                    onChange={handleChangeContactInput}
                  />
                )}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={autocompleteLoading}
              />
            )}
          />
        ) : null}
        <Typography sx={{ fontSize: 17, fontWeight: 600, mt: 2, mb: 2 }}>{t('labels.address')}</Typography>
        {addressWatcher ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Controller
                control={control} name="address.address"
                rules={{ required: isRequired }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('labels.address')}
                    required
                    error={Boolean(errors.address?.address)}
                    helperText={errors.address?.address?.message || ''}
                    multiline
                    rows={4.5}
                  />
                )}
              />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Controller
                  control={control} name="address.city"
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('labels.city')}
                    />
                  )}
                />
                <Controller
                  control={control} name="address.state"
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('labels.state')}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Controller
                  control={control} name="address.zipCode"
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('labels.zipCode')}
                    />
                  )}
                />
                <Controller
                  control={control} name="address.country"
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('labels.country')}
                    />
                  )}
                />
              </Box>
            </Box>
            <IconButton
              name="Remove address"
              onClick={onRemoveAddress}
              sx={{ alignSelf: 'center' }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ) : null}
        {!addressWatcher ? (
          <Button
            name="Add address"
            onClick={onAddAddress}
            size="small"
            startIcon={<AddIcon />}
          >
            {t('buttons.addAddress')}
          </Button>
        ) : null}
        <Typography sx={{ fontSize: 17, fontWeight: 600, mt: 2 }}>{t('labels.phones')}</Typography>
        {fields.map((field, index) => (
          <Box key={field.id} sx={{ display: 'flex', gap: 2, alignItems: 'start', mt: 2 }}>
            <Controller
              control={control} name={`phones.${index}.type`}
              rules={{ required: isRequired }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={phoneTypeOptions}
                  label={t('labels.type')}
                  error={Boolean(errors.phones?.[index]?.type)}
                  helperText={(errors.phones as any)?.[index]?.type?.message || ''}
                  required
                />
              )}
            />
            <Controller
              control={control} name={`phones.${index}.phoneNumber`}
              rules={{ required: isRequired }}
              render={({ field }) => (
                <Phone
                  {...field}
                  label={t('labels.phone')}
                  error={Boolean(errors.phones?.[index]?.phoneNumber)}
                  helperText={(errors.phones as any)?.[index]?.phoneNumber?.message || ''}
                  required
                />
              )}
            />
            <Controller
              control={control} name={`phones.${index}.extension`}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('labels.extension')}
                  type="number"
                />
              )}
            />
            <IconButton
              name={`Remove phone ${index}`}
              onClick={() => onRemovePhone(index)}
              size="small"
              sx={{ mt: 6.5 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          name="Add phone"
          onClick={onAddPhone}
          size="small"
          startIcon={<AddIcon />}
        >
          {t('buttons.addPhone')}
        </Button>
        <Controller
          control={control} name="notes"
          render={({ field }) => (
            <Input
              {...field}
              label={t('labels.notes')}
              multiline
              minRows={2}
              maxRows={4}
            />
          )}
        />
      </form>
    </Dialog>
  )
}

export default ContactsFormDialog;
